// src/vendor/pages/Orders/OrderDetails.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { vendorApi } from "../../services/vendorApi";
import { useVendorAuth } from "../../store/vendorAuthStore";
import "./orderDetails.css";

import jsPDF from "jspdf";
import html2canvas from "html2canvas";

/* ── helpers ── */
function money(n) {
  return "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}
function fmtDate(d) {
  if (!d) return "-";
  var parts = String(d).slice(0, 10).split("-");
  var months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return parts[2] + " " + months[Number(parts[1]) - 1] + " " + parts[0];
}
function unitLabel(unit, unit_qty, variant_size, variant_value) {
  const vv = variant_value ? String(variant_value).trim() : "";

  if (vv) {
    if (unit) return `${unit} (${vv})`;  // "gram (1000gram)"
    return vv;                            // just "1000gram"
  }

  if (variant_size && String(variant_size).trim()) {
    return String(variant_size).trim();
  }

  if (!unit) return "";
  if (unit === "piece") return "piece";

  var qty = Number(unit_qty || 1);
  if (qty > 0 && qty !== 1) return qty + " " + unit;
  return unit;
}

/* ── icons ── */
const IconBack = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const IconPrint = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 6 2 18 2 18 9" />
    <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
    <rect x="6" y="14" width="12" height="8" />
  </svg>
);
const IconDownload = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const IconOrder = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 01-8 0" />
  </svg>
);
const IconUser = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const IconPhone = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.63A2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
  </svg>
);
const IconMapPin = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);
const IconHash = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="4" y1="9" x2="20" y2="9" /><line x1="4" y1="15" x2="20" y2="15" />
    <line x1="10" y1="3" x2="8" y2="21" /><line x1="16" y1="3" x2="14" y2="21" />
  </svg>
);
const IconCalendar = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const IconCreditCard = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);
const IconTag = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

export default function OrderDetails() {
  const nav = useNavigate();
  const { id } = useParams();
  const { token } = useVendorAuth();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [dlErr, setDlErr] = useState("");
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const printRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setErr(""); setLoading(true);
      const res = await vendorApi.orderDetails(token, id);
      console.log("Details Response :", res);
      setOrder(res?.order ?? null);
      setItems(Array.isArray(res?.items) ? res.items : []);
    } catch (e) {
      setErr(e?.message || "Failed to load order details");
      setOrder(null); setItems([]);
    } finally { setLoading(false); }
  }, [token, id]);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    const subtotal = items.reduce((a, x) => {
      const line = x?.line_total != null
        ? Number(x.line_total || 0)
        : Number((x?.price || 0) * (x?.quantity || 0));
      return a + line;
    }, 0);
const TotalPayable = items.reduce((a, x) => {
  return a + Number(x.payable_amount || 0) + Number(x.delivery_charge || 0);
}, 0);

    const delivery = Number(order?.delivery_charge || 0);
    const grand = Number(order?.grand_total || subtotal + delivery);
    return { subtotal, delivery, grand ,TotalPayable};
  }, [items, order]);

  /* PDF download — shows invoice header in the capture */
  const handleDownloadPDF = async () => {
    setDlErr("");
    if (!order) { setDlErr("Order not found"); return; }

    try {
      const logoUrl = "https://xiadot.com/yuroshopy/vendor/logo.png";

      // Build invoice rows
      const itemRows = items.map((x, idx) => {
        const qty = Number(x?.quantity || 0);
        const price = Number(x?.price || 0);
        const total = Number(x?.line_total != null ? x.line_total : qty * price);
        const uLabel =
          x?.variant_value ||
          unitLabel(x?.unit, x?.unit_qty);
        return `
        <tr>
          <td style="padding:8px 10px;border:1px solid #ddd;color:#333;font-size:14px;">${idx + 1}</td>
          <td style="padding:8px 10px;border:1px solid #ddd;font-weight:700;font-size:14px;">${x?.product_name || x?.name || "-"}</td>
          <td style="padding:8px 10px;border:1px solid #ddd;font-size:14px;">
            <span style="display:inline-block;padding:2px 8px;border-radius:4px;background:#f0f9ff;color:#0369a1;font-size:15px;font-weight:600;">${uLabel}</span>
          </td>
          <td style="padding:8px 10px;border:1px solid #ddd;font-size:14px;">×${qty}</td>
          <td style="padding:8px 10px;border:1px solid #ddd;font-size:14px;">₹${price}</td>
          <td style="padding:8px 10px;border:1px solid #ddd;font-size:14px;text-align:right;font-weight:700;">₹${total}</td>
        </tr>`;
      }).join("");

      const subtotal = totals.subtotal;
      const delivery = totals.delivery;
      const grand = totals.grand;

      const html = `
      <div style="font-family:Arial,sans-serif;font-size:14px;color:#000;width:794px;background:#fff;padding:20px;box-sizing:border-box;">

        <!-- HEADER -->
        <div style="display:flex;align-items:stretch;justify-content:space-between;border:1.5px solid #000;margin-bottom:0;overflow:hidden;">
          <div style="display:flex;align-items:center;gap:10px;padding:10px 14px;border-right:1.5px solid #000;white-space:nowrap;">
            <img src="${logoUrl}" width="100" height="100" style="object-fit:contain;border-radius:6px;display:block;" crossorigin="anonymous"/>
            <div>
              <div style="font-size:30px;font-weight:900;color:#000;letter-spacing:-0.5px;line-height:1.1;">YURO SHOPY</div>
              <div style="font-size:13px;color:#555;letter-spacing:0.1em;text-transform:uppercase;margin-top:3px;">Vendor Invoice</div>
            </div>
          </div>
          <div style="display:flex;flex-direction:column;align-items:flex-end;justify-content:center;padding:10px 14px;flex:1;">
            <div style="font-size:13px;font-weight:900;color:#000;text-transform:uppercase;border-bottom:1.5px solid #000;padding-bottom:4px;margin-bottom:5px;width:100%;text-align:right;white-space:nowrap;">
              Tax Invoice / Bill of Supply
            </div>
            <div style="display:flex;justify-content:flex-end;gap:6px;font-size:14px;color:#000;margin-top:2px;width:100%;">
              <span style="color:#555;">Invoice No:</span><span style="font-weight:700;">${orderCode}</span>
            </div>
            <div style="display:flex;justify-content:flex-end;gap:6px;font-size:14px;color:#000;margin-top:2px;width:100%;">
              <span style="color:#555;">Invoice Date:</span><span style="font-weight:700;">${fmtDate(order.created_at)}</span>
            </div>
            <div style="display:flex;justify-content:flex-end;gap:6px;font-size:14px;color:#000;margin-top:2px;width:100%;">
              <span style="color:#555;">Order Date:</span><span style="font-weight:700;">${fmtDate(order.created_at)}</span>
            </div>
          </div>
        </div>

        <!-- CUSTOMER + ORDER INFO -->
        <table style="width:100%;border-collapse:collapse;border:1.5px solid #000;border-top:none;">
          <tr>
            <td style="width:50%;padding:10px 14px;vertical-align:top;border-right:1px solid #ccc;">
              <div style="font-size:15px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;border-bottom:1px solid #ccc;padding-bottom:5px;margin-bottom:8px;">Customer Information</div>
              <div style="margin-bottom:5px;"><span style="font-size:13px;color:#555;min-width:80px;display:inline-block;">Name</span><span style="font-size:15px;font-weight:600;">${order.customer_name || "-"}</span></div>
              <div style="margin-bottom:5px;"><span style="font-size:13px;color:#555;min-width:80px;display:inline-block;">Phone</span><span style="font-size:15px;font-weight:600;">${order.customer_phone || "-"}</span></div>
              <div><span style="font-size:13px;color:#555;min-width:80px;display:inline-block;">Address</span><span style="font-size:15px;font-weight:600;">${order.address || "-"}</span></div>
            </td>
            <td style="width:50%;padding:10px 14px;vertical-align:top;">
              <div style="font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;border-bottom:1px solid #ccc;padding-bottom:5px;margin-bottom:8px;">Order Information</div>
              <div style="margin-bottom:5px;"><span style="font-size:13px;color:#555;min-width:80px;display:inline-block;">Order No</span><span style="font-size:15px;font-weight:600;">${orderCode}</span></div>
              <div style="margin-bottom:5px;"><span style="font-size:13px;color:#555;min-width:80px;display:inline-block;">Order Date</span><span style="font-size:15px;font-weight:600;">${fmtDate(order.created_at)}</span></div>
              <div><span style="font-size:13px;color:#555;min-width:80px;display:inline-block;">Payment Method</span><span style="font-size:15px;font-weight:600;">${String(order.payment_status || "-")}</span></div>
            </td>
          </tr>
        </table>

        <!-- ITEMS TABLE -->
        <div style="border:1.5px solid #000;border-top:none;">
          <div style="padding:7px 12px;border-bottom:1px solid #000;background:#f5f5f5;display:flex;justify-content:space-between;align-items:center;">
            <span style="font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;">Order Items</span>
            <span style="font-size:14px;color:#999;font-weight:600;">${items.length} item${items.length !== 1 ? "s" : ""}</span>
          </div>
          <table style="width:100%;border-collapse:collapse;">
            <thead>
              <tr style="background:#efefef;">
                <th style="padding:7px 10px;border:1px solid #ccc;font-size:14px;text-align:left;font-weight:700;text-transform:uppercase;letter-spacing:0.03em;">S.No</th>
                <th style="padding:7px 10px;border:1px solid #ccc;font-size:14px;text-align:left;font-weight:700;text-transform:uppercase;letter-spacing:0.03em;">Product</th>
                <th style="padding:7px 10px;border:1px solid #ccc;font-size:14px;text-align:left;font-weight:700;text-transform:uppercase;letter-spacing:0.03em;">Unit</th>
                <th style="padding:7px 10px;border:1px solid #ccc;font-size:14px;text-align:left;font-weight:700;text-transform:uppercase;letter-spacing:0.03em;">Qty</th>
                <th style="padding:7px 10px;border:1px solid #ccc;font-size:14px;text-align:left;font-weight:700;text-transform:uppercase;letter-spacing:0.03em;">Price</th>
                <th style="padding:7px 10px;border:1px solid #ccc;font-size:14px;text-align:right;font-weight:700;text-transform:uppercase;letter-spacing:0.03em;">Total</th>
              </tr>
            </thead>
            <tbody>${itemRows}</tbody>
          </table>

          <!-- SUMMARY -->
          <div style="padding:8px 12px;border-top:1px solid #ccc;display:flex;justify-content:flex-end;">
            <div style="width:260px;">
              <div style="display:flex;justify-content:space-between;padding:4px 0;font-size:15px;color:#333;font-weight:600;">
                <span>Subtotal</span><span style="font-weight:700;color:#000;">₹${subtotal}</span>
              </div>
              <div style="display:flex;justify-content:space-between;padding:4px 0;font-size:15px;color:#333;font-weight:600;">
                <span>Delivery Charge</span><span style="font-weight:700;color:#000;">₹${delivery}</span>
              </div>
              <div style="height:1px;background:#999;margin:5px 0;"></div>
              <div style="display:flex;justify-content:space-between;padding:5px 0;font-size:12px;font-weight:900;color:#000;border-top:1.5px solid #000;margin-top:2px;">
                <span>Grand Total</span><span>₹${grand}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- FOOTER -->
        <div style="border:1.5px solid #000;border-top:1px solid #ccc;padding:10px 14px;background:#fafafa;text-align:center;">
          <div style="font-size:14px;font-weight:700;color:#000;margin-bottom:4px;">Thank You for Shopping with YURO SHOPY!</div>
          <div style="font-size:14px;color:#555;">For any queries regarding this invoice, please contact our support team.</div>
        </div>

      </div>`;

      // Render into hidden iframe
      const iframe = document.createElement("iframe");
      Object.assign(iframe.style, {
        position: "fixed", left: "-99999px", top: "0",
        width: "834px", height: "1200px", border: "none",
      });
      document.body.appendChild(iframe);
      iframe.contentDocument.open();
      iframe.contentDocument.write(`<!DOCTYPE html><html><body style="margin:0;padding:0;background:#fff;">${html}</body></html>`);
      iframe.contentDocument.close();

      // Wait for logo image to load
      await new Promise(r => setTimeout(r, 400));

      const canvas = await html2canvas(iframe.contentDocument.body, {
        scale: 2.5,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        width: 834,
      });
      document.body.removeChild(iframe);

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const maxW = pageW - margin * 2;
      const maxH = pageH - margin * 2;
      const imgProps = pdf.getImageProperties(imgData);
      const imgW = maxW;
      const imgH = (imgProps.height * imgW) / imgProps.width;

      if (imgH <= maxH) {
        pdf.addImage(imgData, "PNG", margin, margin, imgW, imgH);
      } else {
        let heightLeft = imgH, position = 0;
        pdf.addImage(imgData, "PNG", margin, margin + position, imgW, imgH);
        heightLeft -= maxH;
        while (heightLeft > 0) {
          pdf.addPage(); position -= maxH;
          pdf.addImage(imgData, "PNG", margin, margin + position, imgW, imgH);
          heightLeft -= maxH;
        }
      }

      pdf.save(order?.order_code ? order.order_code + ".pdf" : "order-" + id + ".pdf");

    } catch (e) {
      console.error("PDF error:", e);
      setDlErr(e?.message || "Download failed");
    }
  };

  const orderCode = order
    ? (order.order_code || "YURO-" + String(order.id || id).padStart(4, "0"))
    : "YURO-" + String(id).padStart(4, "0");

  return (
    <div className="od-page">

      {/* ── Screen-only top bar ── */}
      <div className="od-top">
        <div className="od-top-left">
          <div className="od-icon-box"><IconOrder /></div>
          <div>
            <h2 className="od-title">Order Details</h2>
            <div className="od-sub">{orderCode}</div>
          </div>
        </div>
        <div className="od-actions">
          <button type="button" className="od-btn od-btn-back" onClick={() => nav(-1)}>
            <IconBack /> Back
          </button>
          <button type="button" className="od-btn od-btn-print" onClick={() => window.print()}>
            <IconPrint /> Print
          </button>
          <button type="button" className="od-btn od-btn-download" onClick={handleDownloadPDF}>
            <IconDownload /> Download
          </button>
        </div>
      </div>

      {err && <div className="od-alert">{err}</div>}
      {dlErr && <div className="od-alert">{dlErr}</div>}
      {loading && <div className="od-loading">Loading…</div>}

      {!loading && order && (
        <div ref={printRef} id="print-area">

          {/*
            ════════════════════════════════════════════
            INVOICE HEADER
            Hidden on screen (display:none in CSS).
            Revealed automatically by @media print CSS.
            Also shown via inline style for PDF export.
            ════════════════════════════════════════════
          */}
          <div className="od-invoice-header">

            {/* Left: logo + shop name */}
            <div className="od-inv-left">
              {/* ── CHANGE: real logo image instead of inline SVG ── */}
              <div className="od-inv-logo-box">
                <img
                  src="https://xiadot.com/yuroshopy/vendor/logo.png"
                  alt="Yuro Shopy Logo"
                  style={{ width: 100, height: 100, objectFit: "contain", borderRadius: 8 }}
                />
              </div>
              <div>
                <span className="od-inv-shop-name" >YURO SHOPY</span>
                <span className="od-inv-shop-sub">Order Invoice</span>
              </div>
            </div>

            {/* Right: Tax Invoice heading + meta */}
            <div className="od-inv-right">
              <span className="od-inv-title-label">INVOICE</span>
              <div className="od-inv-meta-row">
                <span className="od-inv-meta-label">Invoice No:</span>
                <span className="od-inv-meta-val">{orderCode}</span>
              </div>
              <div className="od-inv-meta-row">
                <span className="od-inv-meta-label">Invoice Date:</span>
                <span className="od-inv-meta-val">{fmtDate(order.created_at)}</span>
              </div>
              <div className="od-inv-meta-row">
                <span className="od-inv-meta-label">Order Date:</span>
                <span className="od-inv-meta-val">{fmtDate(order.created_at)}</span>
              </div>
            </div>

          </div>
          {/* /od-invoice-header */}

          {/* ── Info Cards Grid ── */}
          <div className="od-grid">

            {/* Customer */}
            <div className="od-card">
              <div className="od-card-title">Customer Information</div>
              <div className="od-row">
                <div className="od-ico"><IconUser /></div>
                <div>
                  <div className="od-label">Name</div>
                  <div className="od-val">{order.customer_name || "-"}</div>
                </div>
              </div>
              <div className="od-row">
                <div className="od-ico"><IconPhone /></div>
                <div>
                  <div className="od-label">Phone</div>
                  <div className="od-val">{order.customer_phone || "-"}</div>
                </div>
              </div>
              <div className="od-row">
                <div className="od-ico"><IconMapPin /></div>
                <div>
                  <div className="od-label">Address</div>
                  <div className="od-val">{order.address || "-"}</div>
                </div>
              </div>
            </div>

            {/* Order Info */}
            <div className="od-card">
              <div className="od-card-title">Order Information</div>
              <div className="od-row">
                <div className="od-ico"><IconHash /></div>
                <div>
                  <div className="od-label">Order No</div>
                  <div className="od-val">{orderCode}</div>
                </div>
              </div>
              <div className="od-row">
                <div className="od-ico"><IconCalendar /></div>
                <div>
                  <div className="od-label">Order Date</div>
                  <div className="od-val">{fmtDate(order.created_at)}</div>
                </div>
              </div>
              <div className="od-row">
                <div className="od-ico"><IconCreditCard /></div>
                <div>
                  <div className="od-label">Payment Method</div>
                  <div className="od-val">{String(order.payment_status || "-")}</div>
                </div>
              </div>
              {/* ── CHANGE: added od-status-row class — hidden on print/PDF ── */}
              <div className="od-row od-status-row">
                <div className="od-ico"><IconTag /></div>
                <div>
                  <div className="od-label">Status</div>
                  <div className="od-pill">{String(order.status || "-").toUpperCase()}</div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Items Table ── */}
          <div className="od-items-card" >
            <div className="od-items-card-header" style={{ padding:'40px' }} >
              <div className="od-card-title" style={{ margin: 0 }}>Order Items</div>
              <span style={{ fontSize: 12, color: "#999", fontWeight: 600 }}>
                {items.length} item{items.length !== 1 ? "s" : ""}
              </span>
            </div>

            <div className="od-table-wrap">
              <table className="od-table">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Product</th>
                    <th>Unit</th>
                    <th>Qty</th>
                    <th>P.Price</th>
                    <th>Commission</th>
                    <th>Your Earnings</th>
                    <th style={{ textAlign: "right" }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((x, idx) => {
                    const qty = Number(x?.quantity || 0);
                    const price = Number(x?.price || 0);
                    const TotalPayable = Number(x?.payable_amount || 0);
                    const total = Number(x?.line_total != null ? x.line_total : qty * price);
                    const uLabel = unitLabel(x?.unit, x?.unit_qty, x?.variant_size, x?.variant_value);
                    return (
                      <tr key={x?.id ?? idx}>
                        <td className="od-td-sno">{idx + 1}</td>
                        <td className="od-td-prod">{x?.product_name || x?.name || "-"}</td>
                        <td>
                          {uLabel
                            ? <span style={{ display: "inline-block", padding: "2px 7px", borderRadius: 5, background: "#f0f9ff", color: "#0369a1", fontSize: 11.5, fontWeight: 600 }}>{uLabel}</span>
                            : <span style={{ color: "#bbb", fontSize: 12 }}>—</span>
                          }
                        </td>
                        <td><span className="od-qty-badge">×{qty}</span></td>
                        <td>{money(price)}</td>
                        <td>{money(x.commission_amount)}</td>
                        <td>{money(x.payable_amount)}</td>
                        <td style={{ textAlign: "right", fontWeight: 700 }}>{money(total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Summary */}
            <div className="od-summary">
              <div className="od-summary-box">
                <div className="od-s-row">
                  <span>Subtotal</span>
                  <span className="od-s-val">{money(totals.subtotal)}</span>
                </div>
                <div className="od-s-row">
                  <span>Delivery Charge</span>
                  <span className="od-s-val">{money(totals.delivery)}</span>
                </div>
                <div className="od-s-divider" />
                <div className="od-s-row od-s-grand">
                  <span>Grand Total</span>
                  <span>{money(totals.grand)}</span>
                </div>
                <div className="od-s-row od-s-grand">
                  <span>Your Total Earnings</span>
                  <span>{money(totals.TotalPayable)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Thank You / Footer ── */}
          <div className="od-thanks">
            <div className="od-thanks-title">Thank You for Shopping with YURO SHOPY!</div>
            <div className="od-thanks-sub">
              For any queries regarding this invoice, please contact our support team.
            </div>
          </div>

        </div>
      )}
    </div>
  );
}